import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

export interface StatementFitTextProps {
  /** 改行を含むステートメント本文。`\n` で行に分割する。 */
  text: string;
  /** 最大フォントサイズ(px)。既定 26（Django のキャップに合わせる）。 */
  maxFontSize?: number;
  /** 最小フォントサイズ(px)。チャンク分割で十分小さくなるため低めに。 */
  minFontSize?: number;
  className?: string;
}

/** モバイル判定の境界（Django profile_statement.js と同じ 768px）。 */
const MOBILE_BREAKPOINT = 768;
/** 1 行あたりの最大文字数（Django: PC 100 / モバイル 40）。 */
const CHARS_DESKTOP = 100;
const CHARS_MOBILE = 40;

/** 文字列を最大 size 文字ごとに分割（サロゲートペア対応）。 */
const chunk = (line: string, size: number): string[] => {
  const chars = Array.from(line);
  if (chars.length <= size) return [line];
  const out: string[] = [];
  for (let i = 0; i < chars.length; i += size) {
    out.push(chars.slice(i, i + size).join(""));
  }
  return out;
};

/**
 * 「改行位置を尊重し、段落全体を 1 つのフォントサイズで枠幅に最適化」する
 * ステートメント表示（Django profile_statement.js `resizeQuote` 準拠）。
 *
 * - 著者の改行（`\n`）を保持しつつ、長い行は PC 100 文字 / モバイル 40 文字で
 *   再分割する（極端に小さいフォントになるのを防ぐ）。
 * - 最大幅の行（最大文字数行）がコンテナ幅に収まる最大フォントサイズを実測の
 *   二分探索で求め、段落全体の全行へ同一サイズで適用する（上限 maxFontSize）。
 * - `letter-spacing`（フォントに比例しない）も実測に含めるため、はみ出さない。
 * - `ResizeObserver` + window resize + フォント確定後の再計測でビューポートに
 *   追従する。
 */
const StatementFitText: React.FC<StatementFitTextProps> = ({
  text,
  maxFontSize = 26,
  minFontSize = 8,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // モバイル幅かどうか。window 幅の変化（モバイル境界跨ぎ）で分割文字数を切替える。
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT,
  );

  // 幅に応じた分割文字数（PC 100 / モバイル 40）で行を構築する。
  const lines = useMemo(() => {
    const size = isNarrow ? CHARS_MOBILE : CHARS_DESKTOP;
    const out: string[] = [];
    (text || "").split("\n").forEach((line) => {
      chunk(line, size).forEach((c) => out.push(c));
    });
    return out;
  }, [text, isNarrow]);

  const [fontSize, setFontSize] = useState<number>(maxFontSize);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const width = container.clientWidth;
      if (!width) return;

      const cs = window.getComputedStyle(container);
      const probe = document.createElement("span");
      probe.style.position = "absolute";
      probe.style.left = "-9999px";
      probe.style.top = "0";
      probe.style.visibility = "hidden";
      probe.style.whiteSpace = "nowrap";
      probe.style.fontFamily = cs.fontFamily;
      probe.style.fontWeight = cs.fontWeight;
      probe.style.letterSpacing = cs.letterSpacing;
      probe.style.fontStyle = cs.fontStyle;
      document.body.appendChild(probe);

      const measureMaxWidth = (fontPx: number): number => {
        probe.style.fontSize = `${fontPx}px`;
        let m = 0;
        for (const line of lines) {
          probe.textContent = line === "" ? "\u00A0" : line;
          m = Math.max(m, probe.getBoundingClientRect().width);
        }
        return m;
      };

      const safeWidth = Math.max(0, width - 2);
      let best = minFontSize;
      if (measureMaxWidth(maxFontSize) <= safeWidth) {
        best = maxFontSize;
      } else {
        let lo = minFontSize;
        let hi = maxFontSize;
        while (lo <= hi) {
          const mid = Math.floor((lo + hi) / 2);
          if (measureMaxWidth(mid) <= safeWidth) {
            best = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }
      }

      document.body.removeChild(probe);
      setFontSize(best);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => measure()).catch(() => {});
    }
    return () => ro.disconnect();
  }, [lines, minFontSize, maxFontSize]);

  // window 幅変化（モバイル境界跨ぎ）で分割文字数を切り替える。
  useLayoutEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        minWidth: 0,
        flex: "1 1 0%",
        width: "100%",
        fontSize: `${fontSize}px`,
      }}
    >
      {lines.map((line, i) => (
        <div key={i} style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
          {line === "" ? "\u00A0" : line}
        </div>
      ))}
    </div>
  );
};

export default StatementFitText;
